import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Play, BookOpen, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input, Textarea } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

interface Match {
  match: string;
  index: number;
  groups: string[];
}

export const RegexTesterTool: React.FC = () => {
  const { t } = useTranslation();
  const [pattern, setPattern] = useState('');
  const [flags, setFlags] = useState('g');
  const [testString, setTestString] = useState('');
  const [matches, setMatches] = useState<Match[]>([]);
  const [error, setError] = useState('');
  const [isValid, setIsValid] = useState(false);

  const testRegex = () => {
    if (!pattern || !testString) {
      setMatches([]);
      setError('');
      setIsValid(false);
      return;
    }

    try {
      const regex = new RegExp(pattern, flags);
      const results: Match[] = [];
      let match;

      if (flags.includes('g')) {
        while ((match = regex.exec(testString)) !== null) {
          results.push({
            match: match[0],
            index: match.index,
            groups: match.slice(1),
          });
          
          // Prevent infinite loop
          if (match.index === regex.lastIndex) {
            regex.lastIndex++;
          }
        }
      } else {
        match = regex.exec(testString);
        if (match) {
          results.push({
            match: match[0],
            index: match.index,
            groups: match.slice(1),
          });
        }
      }

      setMatches(results);
      setError('');
      setIsValid(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('tools.regexTester.invalidRegex');
      setError(errorMessage);
      setMatches([]);
      setIsValid(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(testRegex, 300);
    return () => clearTimeout(timeoutId);
  }, [pattern, flags, testString]);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopyToClipboard'));
    }
  };

  const highlightMatches = (text: string, matches: Match[]) => {
    if (matches.length === 0) return text;

    let result = '';
    let lastIndex = 0;

    matches.forEach((match, i) => {
      result += text.slice(lastIndex, match.index);
      result += `<mark class="bg-yellow-200 dark:bg-yellow-800 px-1 rounded">${match.match}</mark>`;
      lastIndex = match.index + match.match.length;
    });

    result += text.slice(lastIndex);
    return result;
  };

  const commonPatterns = [
    { name: t('tools.regexTester.patterns.email'), pattern: '[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}', flags: 'g' },
    { name: t('tools.regexTester.patterns.url'), pattern: 'https?:\\/\\/(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@:%_\\+.~#?&//=]*)', flags: 'g' },
    { name: t('tools.regexTester.patterns.phone'), pattern: '\\(?([0-9]{3})\\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})', flags: 'g' },
    { name: t('tools.regexTester.patterns.ipAddress'), pattern: '\\b(?:[0-9]{1,3}\\.){3}[0-9]{1,3}\\b', flags: 'g' },
    { name: t('tools.regexTester.patterns.hexColor'), pattern: '#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})', flags: 'g' },
    { name: t('tools.regexTester.patterns.date'), pattern: '\\d{4}-\\d{2}-\\d{2}', flags: 'g' },
    { name: t('tools.regexTester.patterns.creditCard'), pattern: '\\b(?:\\d{4}[-\\s]?){3}\\d{4}\\b', flags: 'g' },
    { name: t('tools.regexTester.patterns.htmlTags'), pattern: '<[^>]+>', flags: 'g' },
  ];

  const sampleTexts = [
    t('tools.regexTester.samples.email'),
    t('tools.regexTester.samples.phone'),
    t('tools.regexTester.samples.ip'),
    t('tools.regexTester.samples.colors'),
    t('tools.regexTester.samples.dates'),
    t('tools.regexTester.samples.html'),
  ];

  const loadPattern = (patternData: { pattern: string; flags: string }) => {
    setPattern(patternData.pattern);
    setFlags(patternData.flags);
  };

  const loadSampleText = (text: string) => {
    setTestString(text);
  };

  const clearAll = () => {
    setPattern('');
    setFlags('g');
    setTestString('');
    setMatches([]);
    setError('');
    setIsValid(false);
  };

  return (
    <div className="space-y-6">
      {/* Pattern Input */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <span>{t('tools.regexTester.name')}</span>
            {isValid && <CheckCircle className="w-5 h-5 text-green-500" />}
            {error && <AlertCircle className="w-5 h-5 text-red-500" />}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-3">
                <Input
                  label={t('tools.regexTester.pattern')}
                  value={pattern}
                  onChange={(e) => setPattern(e.target.value)}
                  placeholder={t('tools.regexTester.patternPlaceholder')}
                  className="font-mono"
                />
              </div>
              <div>
                <Input
                  label={t('tools.regexTester.flags')}
                  value={flags}
                  onChange={(e) => setFlags(e.target.value)}
                  placeholder={t('tools.regexTester.flagsPlaceholder')}
                  className="font-mono"
                />
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:border-red-800">
                <div className="flex items-center">
                  <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                  <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={testRegex}
                disabled={!pattern || !testString}
              >
                <Play className="w-4 h-4 mr-2" />
                {t('tools.regexTester.testRegex')}
              </Button>
              <Button
                onClick={clearAll}
                variant="outline"
              >
                {t('tools.regexTester.clearAll')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Common Patterns */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BookOpen className="w-5 h-5" />
            <span>{t('tools.regexTester.commonPatterns')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {commonPatterns.map((item, index) => (
              <Button
                key={index}
                onClick={() => loadPattern(item)}
                variant="outline"
                size="sm"
                className="justify-start text-left"
              >
                <span className="font-medium">{item.name}</span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Test String */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tools.regexTester.testString')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Textarea
              value={testString}
              onChange={(e) => setTestString(e.target.value)}
              placeholder={t('tools.regexTester.testStringPlaceholder')}
              className="min-h-[150px] font-mono text-sm"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tools.regexTester.sampleTexts')}:
              </label>
              <div className="space-y-1">
                {sampleTexts.map((text, index) => (
                  <Button
                    key={index}
                    onClick={() => loadSampleText(text)}
                    variant="outline"
                    size="sm"
                    className="w-full justify-start text-left font-mono text-xs"
                  >
                    {text}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {testString && pattern && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t('tools.regexTester.matches')} ({matches.length})</span>
              {matches.length > 0 && (
                <Button
                  onClick={() => copyToClipboard(matches.map(m => m.match).join('\n'))}
                  size="sm"
                  variant="outline"
                >
                  <Copy className="w-3 h-3 mr-1" />
                  {t('tools.regexTester.copyAll')}
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {matches.length > 0 ? (
              <div className="space-y-4">
                {/* Highlighted Text */}
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                    {t('tools.regexTester.highlightedMatches')}
                  </h4>
                  <div 
                    className="p-3 bg-gray-50 dark:bg-gray-800 rounded border font-mono text-sm whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{ __html: highlightMatches(testString, matches) }}
                  />
                </div>

                {/* Match Details */}
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                    {t('tools.regexTester.matchDetails')}
                  </h4>
                  <div className="space-y-2">
                    {matches.map((match, index) => (
                      <div
                        key={index}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg p-3"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {t('tools.regexTester.match')} {index + 1}
                          </span>
                          <Button
                            onClick={() => copyToClipboard(match.match)}
                            size="sm"
                            variant="outline"
                          >
                            <Copy className="w-3 h-3 mr-1" />
                            Copy
                          </Button>
                        </div>
                        <div className="space-y-1 text-sm">
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">{t('tools.regexTester.match')}:</span>
                            <span className="ml-2 font-mono bg-yellow-100 dark:bg-yellow-900/20 px-1 rounded">
                              {match.match}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">{t('tools.regexTester.position')}:</span>
                            <span className="ml-2 font-mono">{match.index}</span>
                          </div>
                          {match.groups.length > 0 && (
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">{t('tools.regexTester.groups')}:</span>
                              <div className="ml-2 space-y-1">
                                {match.groups.map((group, groupIndex) => (
                                  <div key={groupIndex} className="font-mono text-xs">
                                    {t('tools.regexTester.group')} {groupIndex + 1}:
                                    <span className="ml-1 bg-blue-100 dark:bg-blue-900/20 px-1 rounded">
                                      {group || t('tools.regexTester.empty')}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                {pattern && testString ? t('tools.regexTester.noMatches') : t('tools.regexTester.enterPatternAndText')}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Regex Reference */}
      <Card>
        <CardHeader>
          <CardTitle>Regex Quick Reference</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Character Classes</h4>
              <div className="space-y-1 font-mono">
                <div><code>.</code> - Any character except newline</div>
                <div><code>\d</code> - Any digit (0-9)</div>
                <div><code>\w</code> - Any word character (a-z, A-Z, 0-9, _)</div>
                <div><code>\s</code> - Any whitespace character</div>
                <div><code>[abc]</code> - Any of a, b, or c</div>
                <div><code>[a-z]</code> - Any lowercase letter</div>
                <div><code>[^abc]</code> - Not a, b, or c</div>
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Quantifiers</h4>
              <div className="space-y-1 font-mono">
                <div><code>*</code> - 0 or more</div>
                <div><code>+</code> - 1 or more</div>
                <div><code>?</code> - 0 or 1</div>
                <div><code>{`{3}`}</code> - Exactly 3</div>
                <div><code>{`{3,}`}</code> - 3 or more</div>
                <div><code>{`{3,5}`}</code> - Between 3 and 5</div>
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Anchors</h4>
              <div className="space-y-1 font-mono">
                <div><code>^</code> - Start of string</div>
                <div><code>$</code> - End of string</div>
                <div><code>\b</code> - Word boundary</div>
                <div><code>\B</code> - Not word boundary</div>
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Flags</h4>
              <div className="space-y-1 font-mono">
                <div><code>g</code> - Global (find all matches)</div>
                <div><code>i</code> - Case insensitive</div>
                <div><code>m</code> - Multiline</div>
                <div><code>u</code> - Unicode</div>
                <div><code>y</code> - Sticky</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
